import axios, { AxiosError, AxiosRequestConfig } from "axios";
import { get } from "lodash";

import { getCookie } from "lib/helpers";

import {
  defaultErrorMessage,
  defaultPostOptions,
  defaultRetryContactMessage,
  notFoundErrorMessage,
  unauthorizedErrorMessage,
} from "./constants";

const instance = axios.create({
  baseURL: "",
  timeout: 20000,
  withCredentials: true,
});

instance.interceptors.request.use(config => {
  const CSRFToken = getCookie("csrftoken");
  return {
    ...config,
    headers: {
      ...config.headers,
      "X-CSRFToken": CSRFToken,
    },
  };
});

export function getErrorMessage(response: any) {
  const error = get(response, "details", response[Object.keys(response)[0]]);

  if (Array.isArray(error)) {
    return error[0];
  }

  if (error === null) {
    return get(response, "error", null);
  }

  return error;
}

const getRequestErrorMessage = (error: AxiosError): string => {
  const status = error.response?.status || 500;
  if (status >= 500) {
    return `${defaultErrorMessage} ${defaultRetryContactMessage}`;
  }

  const response = error.response?.data;
  const errorMessageResponse = getErrorMessage(response);

  if (typeof response === "object") {
    return errorMessageResponse || error.message;
  }
  if (typeof response === "string") {
    return response;
  }
  return defaultErrorMessage;
};

export const makeSimpleGetRequest = async <T>(
  url: string,
  requestOptions: AxiosRequestConfig = {},
): Promise<T> => {
  try {
    const request = await instance.get(url, requestOptions);
    const response = request.data;
    return response;
  } catch (e) {
    const error = e as AxiosError;
    if (error.response?.status === 403) {
      throw Error(unauthorizedErrorMessage);
    }
    if (error.response?.status === 404) {
      throw Error(notFoundErrorMessage);
    }
    throw Error(defaultErrorMessage);
  }
};

export const makeSimplePostRequest = async <T>(
  url: string,
  payload: string | FormData,
  allowedStatuses: number[],
  requestOptions?: AxiosRequestConfig,
  responsePayloadPath = "data",
): Promise<T> => {
  try {
    const response = await instance.post(
      url,
      payload,
      requestOptions ?? defaultPostOptions,
    );
    if (allowedStatuses.includes(response.status)) {
      return get(response, responsePayloadPath);
    }
    throw Error(JSON.stringify(response));
  } catch (e) {
    const error = e as AxiosError;
    throw Error(getRequestErrorMessage(error));
  }
};

export const makeSimpleDeleteRequest = async <T>(
  url: string,
  allowedStatuses: number[],
  requestOptions?: AxiosRequestConfig,
): Promise<T> => {
  try {
    const request = await instance.delete(
      url,
      requestOptions ?? defaultPostOptions,
    );
    const response = request.data;
    if (allowedStatuses.includes(request.status)) {
      return response;
    }
    throw Error(JSON.stringify(response));
  } catch (e) {
    const error = e as AxiosError;
    throw Error(getRequestErrorMessage(error));
  }
};

export const makeSimplePatchRequest = async <T>(
  url: string,
  payload: string,
  allowedStatuses: number[],
  requestOptions?: AxiosRequestConfig,
): Promise<T> => {
  try {
    const request = await instance.patch(
      url,
      payload,
      requestOptions ?? defaultPostOptions,
    );
    const response = request.data;
    if (allowedStatuses.includes(request.status)) {
      return response;
    }
    throw Error(JSON.stringify(response));
  } catch (e) {
    const error = e as AxiosError;
    throw Error(getRequestErrorMessage(error));
  }
};

export const makeSimpleHeadRequest = async <T>(
  url: string,
  requestOptions: AxiosRequestConfig = {},
): Promise<T> => {
  try {
    const response = await instance.head(url, requestOptions);
    if (response.status >= 300) {
      throw Error();
    }
    return response.request;
  } catch (e) {
    throw Error(defaultErrorMessage);
  }
};

export default instance;
